// VERSION: 2025-01-07-SCALE-FIX-v2
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import HTMLFlipBook from 'react-pageflip';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';

// Configurar el worker de PDF.js
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js`;

// Componente de página separado para no recrearse en cada render
const Page = React.forwardRef(({ pageData, className, flipBookRef }, ref) => {
  const imgRef = useRef(null);
  const containerRef = useRef(null);
  const [linkBoxes, setLinkBoxes] = useState([]); // almacenar cajas en % relativos a la imagen base
  const [activeVideo, setActiveVideo] = useState(null); // embed URL para modal de video
  const computedOnceRef = useRef(false); // bandera para calcular solo una vez al montar
  const [displaySize, setDisplaySize] = useState({ w: 0, h: 0 });

  // Detectar YouTube y convertir a URL de embed. Si no es YouTube, abrir en pestaña nueva.
  const getYouTubeEmbedUrl = useCallback((url) => {
    if (!url) return null;
    try {
      const u = new URL(url, window.location.href);
      const host = u.hostname.toLowerCase();
      let id = null;
      if (host.includes('youtube.com')) {
        // ejemplo: /watch?v=ID
        id = u.searchParams.get('v');
      } else if (host === 'youtu.be') {
        // ejemplo: youtu.be/ID
        id = u.pathname.slice(1);
      }
      if (!id) return null;
      const embed = `https://www.youtube.com/embed/${encodeURIComponent(id)}?rel=0&showinfo=0&autoplay=1`;
      return embed;
    } catch (e) {
      return null;
    }
  }, []);

  const handleLinkClick = useCallback((link) => {
    // Manejar enlaces internos (destinos dentro del PDF)
    if (link.dest && flipBookRef?.current) {
      try {
        // Navegar a la página de destino
        const targetPage = link.destPageNumber - 1; // flipbook usa índice base 0
        flipBookRef.current.pageFlip().flip(targetPage);
        return;
      } catch (e) {
        console.warn('Error al navegar a página interna:', e);
      }
    }
    
    const url = link.url || link;
    // Si es enlace YouTube, abrir en overlay con iframe
    const embed = getYouTubeEmbedUrl(url);
    if (embed) {
      try {
        setActiveVideo(embed);
        return;
      } catch (e) {
        // si falla, caer al comportamiento por defecto
      }
    }
    // Abrir el link en una nueva pestaña para otros dominios
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [getYouTubeEmbedUrl, flipBookRef]);

  // Mantener el tamaño mostrado del <img> sincronizado (wrapper igual al img)
  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;
    const update = () => {
      const w = img.clientWidth || img.width || 0;
      const h = img.clientHeight || img.height || 0;
      setDisplaySize({ w, h });
      // permitir recalcular cajas si cambió tamaño
      computedOnceRef.current = false;
    };
    update();
    // Observar cambios de tamaño del img
    let ro;
    try {
      ro = new ResizeObserver(() => {
        update();
      });
      ro.observe(img);
    } catch (e) {
      // ResizeObserver puede no estar disponible en algunos entornos
      window.addEventListener('resize', update);
    }
    return () => {
      try { if (ro && img) ro.unobserve(img); } catch(_) {}
      try { window.removeEventListener('resize', update); } catch(_) {}
    };
  }, [imgRef]);

  // Calcular overlays UNA SOLA VEZ al cargar la imagen (usando dimensiones naturales)
  const computeLinkBoxesOnce = useCallback(() => {
    if (!pageData || !pageData.links || !imgRef.current || computedOnceRef.current) return;
    const imgEl = imgRef.current;

    // Tamaño real mostrado (no natural) dentro del flipbook
    const displayW = imgEl.clientWidth;
    const displayH = imgEl.clientHeight;
    if (displayW < 10 || displayH < 10) return; // esperar layout

    const baseW = pageData.width;   // ancho del canvas original renderizado
    const baseH = pageData.height;  // alto del canvas original renderizado
    if (!baseW || !baseH) return;

    const scaleX = displayW / baseW;
    const scaleY = displayH / baseH;

    const newBoxes = pageData.links.map(link => {
      if (!link.viewportRect) return null;
      const [x1, y1, x2, y2] = link.viewportRect; // suponemos coordenadas top-left pero validamos
      // Detectar si viene invertido verticalmente: si y1 > baseH o valores negativos raros
      let topSrc = y1;
      let bottomSrc = y2;
      if (y1 > y2) { // swap si vienen invertidos
        topSrc = y2; bottomSrc = y1;
      }
      // Si parece estar en sistema invertido (origin bottom-left) porque topSrc > baseH/2 y bottomSrc > baseH
      if (bottomSrc > baseH) {
        // Reinterpretar como coordenadas bottom-left: newTop = baseH - maxY
        const maxY = Math.max(y1, y2);
        const minY = Math.min(y1, y2);
        const hRaw = Math.abs(y2 - y1);
        topSrc = baseH - maxY;
        bottomSrc = topSrc + hRaw;
      }
      // Clamp
      topSrc = Math.max(0, Math.min(baseH, topSrc));
      bottomSrc = Math.max(topSrc, Math.min(baseH, bottomSrc));
      const leftSrc = Math.min(x1, x2);
      const rightSrc = Math.max(x1, x2);
      const wSrc = Math.max(1, rightSrc - leftSrc);
      const hSrc = Math.max(1, bottomSrc - topSrc);

      // Convertir desde coordenadas del canvas a coordenadas dentro del img DOM (tienen offset dentro de imgRect)
      const left = leftSrc * scaleX;
      const top = topSrc * scaleY;
      const w = wSrc * scaleX;
      const h = hSrc * scaleY;

      // Si el overlay queda fuera verticalmente, fallback a porcentajes previos (si existen)
      if ((top + h) > displayH + 2 || top < -2) {
        if (link.pct) {
          const boxPct = {
            url: link.url,
            dest: link.dest,
            destPageNumber: link.destPageNumber,
            style: {
              position: 'absolute',
              left: `${link.pct.left}%`,
              top: `${link.pct.top}%`,
              width: `${link.pct.width}%`,
              height: `${link.pct.height}%`,
              boxSizing: 'border-box',
              zIndex: 10
            },
            className: 'link-overlay'
          };
          console.warn('[link-box-fallback-pct] page', pageData.pageNumber, link.url, { pct: link.pct, displayH, baseH });
      // debug info removed: previously pushed to window.__linkDebug
          return boxPct;
        }
      }

      const box = {
        url: link.url,
        dest: link.dest,
        destPageNumber: link.destPageNumber,
        style: {
          position: 'absolute',
          left: `${left}px`,
          top: `${top}px`,
          width: `${w}px`,
          height: `${h}px`,
          boxSizing: 'border-box',
          zIndex: 10
        },
        className: 'link-overlay'
      };
      console.debug('[link-box-px] page', pageData.pageNumber, link.url, { baseW, baseH, displayW, displayH, scaleX: scaleX.toFixed(3), scaleY: scaleY.toFixed(3), srcRect: link.viewportRect, normalized: { leftSrc, topSrc, wSrc, hSrc }, placed: { left, top, w, h } });
      // debug info removed: previously pushed to window.__linkDebug
      return box;
    }).filter(Boolean);

    setLinkBoxes(newBoxes);
    computedOnceRef.current = true;
  }, [pageData]);

  // Recalcular cuando cambie displaySize (por ejemplo al redimensionar el flipbook)
  useEffect(() => {
    computedOnceRef.current = false;
    setLinkBoxes([]);
    computeLinkBoxesOnce();
  }, [computeLinkBoxesOnce, displaySize]);

  // Resetear la bandera cuando cambia pageData (página diferente)
  useEffect(() => {
    computedOnceRef.current = false;
    setLinkBoxes([]);
  }, [pageData]);

  // Compute once on mount or when image loads
  useEffect(() => {
    computeLinkBoxesOnce();
  }, [computeLinkBoxesOnce]);  return (
    <div ref={(el) => { containerRef.current = el; if (typeof ref === 'function') ref(el); else if (ref) ref.current = el; }} className={`page-content ${className || ''}`} style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
      {pageData && (
        <div style={{ position: 'relative', maxWidth: '100%', maxHeight: '100%', display: 'inline-block' }}>
          <img
            ref={imgRef}
            src={pageData.imageUrl}
            alt={`Página ${pageData.pageNumber}`}
            style={{ display: 'block', maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto' }}
            onLoad={() => {
              // Computar cajas una vez cuando la imagen haya cargado
              requestAnimationFrame(() => computeLinkBoxesOnce());
            }}
          />

          {/* Capa de links sobre la imagen: porcentajes relativos a la imagen, no al contenedor padre */}
          {linkBoxes && linkBoxes.length > 0 && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none' // Permitir clicks salvo en los hijos
              }}
            >
              {linkBoxes.map((box, idx) => (
                <a
                  key={idx}
                  href={box.url || '#'}
                  target={box.dest ? undefined : "_blank"}
                  rel={box.dest ? undefined : "noopener noreferrer"}
                  // Prevent the flipbook from receiving pointer/click events when interacting with links
                  onClickCapture={(e) => { e.preventDefault(); e.stopPropagation(); try { e.nativeEvent && e.nativeEvent.stopImmediatePropagation && e.nativeEvent.stopImmediatePropagation(); } catch(_){}; handleLinkClick(box); }}
                  onPointerDownCapture={(e) => { e.stopPropagation(); }}
                  onMouseDownCapture={(e) => { e.stopPropagation(); }}
                  onTouchStartCapture={(e) => { e.stopPropagation(); }}
                  onPointerDown={(e) => { e.stopPropagation(); }}
                  onMouseDown={(e) => { e.stopPropagation(); }}
                  onTouchStart={(e) => { e.stopPropagation(); }}
                  style={box.style}
                  className={box.className}
                  title={box.dest ? `Ir a página ${box.destPageNumber}` : box.url}
                  aria-label={box.dest ? `Enlace interno a página ${box.destPageNumber}` : `Link a ${box.url}`}
                />
              ))}
            </div>
          )}

          {/* Overlay modal para reproducir videos embebidos (YouTube) dentro de la página */}
          {activeVideo && (
            <div
              role="dialog"
              aria-modal="true"
              className="video-overlay"
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); setActiveVideo(null); }}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0,0,0,0.65)',
                zIndex: 99999,
                pointerEvents: 'auto'
              }}
            >
              <div
                onClick={(e) => { e.stopPropagation(); }}
                style={{
                  width: '80%',
                  maxWidth: '1100px',
                  height: '60%',
                  maxHeight: '720px',
                  background: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative'
                }}
              >
                <iframe
                  title="YouTube video"
                  src={activeVideo}
                  allow="autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                  style={{ width: '100%', height: '100%', border: 'none', borderRadius: 6 }}
                />
                <button
                  aria-label="Cerrar video"
                  onClick={() => setActiveVideo(null)}
                  style={{
                    position: 'absolute',
                    top: -10,
                    right: -10,
                    background: '#111',
                    color: '#fff',
                    border: 'none',
                    width: 36,
                    height: 36,
                    borderRadius: 18,
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.4)'
                  }}
                >
                  ✕
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

  const Flipbook = ({ pdfUrl }) => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenHeight, setFullscreenHeight] = useState(null);
  const flipBookRef = useRef();
  const containerRef = useRef(null);
  const controlBarRef = useRef(null);
  const [controlBarHeight, setControlBarHeight] = useState(0);
  const [contentAvailable, setContentAvailable] = useState(null);
  const [needBottomPadding, setNeedBottomPadding] = useState(false);
  const [isMobile, setIsMobile] = useState(false);  // Detect if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Compute available height for the flipbook content (container height minus control bar)
  useEffect(() => {
    const update = () => {
      try {
        const contH = containerRef.current ? containerRef.current.clientHeight : window.innerHeight;
        const barH = controlBarRef.current ? controlBarRef.current.offsetHeight : 64;
        const avail = Math.max(120, contH - barH - 16);
        setContentAvailable(avail);
      } catch (e) {
        setContentAvailable(window.innerHeight - 120);
      }
    };
    update();
    window.addEventListener('resize', update);
    document.addEventListener('fullscreenchange', update);
    // vendor prefixes
    document.addEventListener('webkitfullscreenchange', update);
    document.addEventListener('mozfullscreenchange', update);
    document.addEventListener('MSFullscreenChange', update);
    return () => {
      window.removeEventListener('resize', update);
      document.removeEventListener('fullscreenchange', update);
      document.removeEventListener('webkitfullscreenchange', update);
      document.removeEventListener('mozfullscreenchange', update);
      document.removeEventListener('MSFullscreenChange', update);
    };
  }, []);

  // Helper to update all measurements (control bar height, contentAvailable, fullscreenHeight)
  const updateMeasurements = useCallback(() => {
    try {
      const barH = controlBarRef.current ? controlBarRef.current.offsetHeight : 0;
      setControlBarHeight(barH || 0);
      const contH = containerRef.current ? containerRef.current.clientHeight : window.innerHeight;
      const avail = Math.max(120, contH - (barH || 64) - 16);
      setContentAvailable(avail);
      // recalc fullscreenHeight too
      if (document.fullscreenElement === containerRef.current) {
        const winH = typeof window !== 'undefined' ? window.innerHeight : contH;
        const availableFs = Math.max(120, winH - (barH || 64) - 12);
        setFullscreenHeight(availableFs);
      } else {
        // leave fullscreenHeight as is
      }
      // ask flipbook to update layout if present
      try { flipBookRef.current && flipBookRef.current.pageFlip().update(); } catch(_){}
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (pdfUrl) {
      loadPDF(pdfUrl);
    }
  }, [pdfUrl]);

  // After pages load, re-run measurements a few times to avoid initial mis-measure
  useEffect(() => {
    if (!pages || pages.length === 0) return;
    // run immediate + retries to allow layout to stabilize
    updateMeasurements();
    const t1 = setTimeout(updateMeasurements, 100);
    const t2 = setTimeout(updateMeasurements, 300);
    const t3 = setTimeout(updateMeasurements, 700);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [pages, updateMeasurements]);

  // Calcular altura disponible en fullscreen
  const calculateFullscreenHeight = useCallback(() => {
    if (!isFullscreen || !containerRef.current || !controlBarRef.current) return;
    const containerH = containerRef.current.clientHeight;
    const barH = controlBarRef.current.offsetHeight;
    // Restar barra y un pequeño padding superior
    const available = containerH - barH - 8;
    setFullscreenHeight(available);
  }, [isFullscreen]);

  // Sincronizar estado al cambiar fullscreen (ESC, F11, etc.)
  useEffect(() => {
    const handleFsChange = () => {
      const fsElement = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement;
      const active = !!fsElement && fsElement === containerRef.current;
      setIsFullscreen(active);
      // Forzar recalculo del flipbook tras un pequeño delay para layout estable
      if (flipBookRef.current) {
        setTimeout(() => {
          calculateFullscreenHeight();
          try {
            flipBookRef.current.pageFlip().update();
          } catch (_) {}
          // disparar resize para librerías que escuchan
          window.dispatchEvent(new Event('resize'));
        }, 120);
      }
    };
    document.addEventListener('fullscreenchange', handleFsChange);
    // vendor prefijos por soporte amplio
    document.addEventListener('webkitfullscreenchange', handleFsChange);
    document.addEventListener('mozfullscreenchange', handleFsChange);
    document.addEventListener('MSFullscreenChange', handleFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFsChange);
      document.removeEventListener('webkitfullscreenchange', handleFsChange);
      document.removeEventListener('mozfullscreenchange', handleFsChange);
      document.removeEventListener('MSFullscreenChange', handleFsChange);
    };
  }, [calculateFullscreenHeight]);

  // Measure control bar height and keep it updated
  useEffect(() => {
    const updateBar = () => {
      try {
        const h = controlBarRef.current ? controlBarRef.current.offsetHeight : 0;
        setControlBarHeight(h || 0);
      } catch (e) {
        setControlBarHeight(0);
      }
    };
    updateBar();
    window.addEventListener('resize', updateBar);
    document.addEventListener('fullscreenchange', updateBar);
    document.addEventListener('webkitfullscreenchange', updateBar);
    document.addEventListener('mozfullscreenchange', updateBar);
    document.addEventListener('MSFullscreenChange', updateBar);
    return () => {
      window.removeEventListener('resize', updateBar);
      document.removeEventListener('fullscreenchange', updateBar);
      document.removeEventListener('webkitfullscreenchange', updateBar);
      document.removeEventListener('mozfullscreenchange', updateBar);
      document.removeEventListener('MSFullscreenChange', updateBar);
    };
  }, []);

  // Decide if we actually need to reserve padding bottom: only when flipbook height would overlap the control bar
  useEffect(() => {
    const defaultPageHeight = 480 + 12; // same buffer used for normal mode
    let requiredFlipHeight = defaultPageHeight;
    if (isFullscreen) {
      // estimate fullscreen flipbook height similar to flipBookComponent logic
      const ratio = pages[0] ? pages[0].width / pages[0].height : 0.75;
      const estPageHeight = fullscreenHeight ? Math.min(fullscreenHeight - 20, window.innerHeight - 80) : Math.min(window.innerHeight - 80, 720);
      requiredFlipHeight = Math.round(estPageHeight + 12);
    }
    // If the content area (container height) minus required flip height is less than controlBarHeight + safety, we need padding
    const contH = containerRef.current ? containerRef.current.clientHeight : window.innerHeight;
    const spaceBelow = contH - requiredFlipHeight;
    const safety = 8; // leave small safety space
    const need = spaceBelow < (controlBarHeight + safety);
    // debug
    // console.debug('[flipbook] padding-check', { contH, requiredFlipHeight, spaceBelow, controlBarHeight, need });
    setNeedBottomPadding(!!need);
  }, [controlBarHeight, contentAvailable, isFullscreen, fullscreenHeight, pages]);

  // Force flipbook to update layout when measurements change
  useEffect(() => {
    if (flipBookRef.current) {
      try { flipBookRef.current.pageFlip().update(); } catch(_){}
    }
  }, [contentAvailable, needBottomPadding]);

  // Recalcular en resize solo si está en fullscreen
  useEffect(() => {
    if (!isFullscreen) return;
    const handleResize = () => {
      calculateFullscreenHeight();
      if (flipBookRef.current) {
        try { flipBookRef.current.pageFlip().update(); } catch(_) {}
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isFullscreen, calculateFullscreenHeight]);

  const toggleFullscreen = useCallback(async () => {
    if (!containerRef.current) return;
    try {
      const isActive = document.fullscreenElement === containerRef.current;
      if (!isActive) {
        if (containerRef.current.requestFullscreen) {
          await containerRef.current.requestFullscreen({ navigationUI: 'hide' });
        } else if (containerRef.current.webkitRequestFullscreen) {
          containerRef.current.webkitRequestFullscreen();
        } else if (containerRef.current.mozRequestFullScreen) {
          containerRef.current.mozRequestFullScreen();
        } else if (containerRef.current.msRequestFullscreen) {
          containerRef.current.msRequestFullscreen();
        }
      } else {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }
      }
      if (flipBookRef.current) {
        requestAnimationFrame(() => {
          try { flipBookRef.current.pageFlip().update(); } catch(_) {}
        });
      }
    } catch (err) {
      console.warn('No se pudo cambiar a pantalla completa:', err);
    }
  }, []);

  const loadPDF = async (url) => {
    try {
      setLoading(true);
      setError(null);

      // Cargar el documento PDF
      const loadingTask = pdfjsLib.getDocument(url);
      const pdf = await loadingTask.promise;

      const pagePromises = [];
      
      // Generar promesas para renderizar todas las páginas
      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        pagePromises.push(renderPage(pdf, pageNum));
      }

      // Esperar a que todas las páginas se rendericen
      const renderedPages = await Promise.all(pagePromises);
      setPages(renderedPages);
      setLoading(false);
    } catch (err) {
      console.error('Error loading PDF:', err);
      setError('Error al cargar el PDF. Verifica que el archivo existe.');
      setLoading(false);
    }
  };

  const renderPage = async (pdf, pageNumber) => {
    try {
      const page = await pdf.getPage(pageNumber);
      const viewportBase = page.getViewport({ scale: 1.0 });
      const renderScale = 2.0;
      const viewport = page.getViewport({ scale: renderScale });

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      const renderContext = { canvasContext: context, viewport };
      await page.render(renderContext).promise;
      const imageDataUrl = canvas.toDataURL('image/png');

      const annotations = await page.getAnnotations();
      const links = [];
      for (const annotation of annotations) {
        if (annotation.subtype === 'Link') {
          // Manejar enlaces externos
          const linkUrl = annotation.url || annotation.unsafeUrl || null;
          // Manejar enlaces internos (destinos)
          const dest = annotation.dest || null;
          
          // Necesitamos al menos una URL o un destino
          if (!linkUrl && !dest) continue;
          if (!annotation.rect) continue;
          
          // Si hay destino interno, resolver a qué página apunta
          let destPageNumber = null;
          if (dest) {
            try {
              // Obtener el destino
              let destination = dest;
              if (typeof dest === 'string') {
                destination = await pdf.getDestination(dest);
              }
              
              if (destination && destination[0]) {
                // destination[0] es una referencia a la página
                const destRef = destination[0];
                destPageNumber = await pdf.getPageIndex(destRef) + 1; // +1 porque pageIndex es base 0
              }
            } catch (e) {
              console.warn('Error resolviendo destino interno:', dest, e);
            }
          }
          // Convertir rect a coordenadas del viewport escalado directamente (ya con origen arriba-izquierda)
          // Nota: convertToViewportRectangle devuelve [x1, y1, x2, y2] en coordenadas CSS del viewport actual
          let vpRect;
          try {
            vpRect = viewport.convertToViewportRectangle(annotation.rect.slice());
          } catch (e) {
            vpRect = null;
          }
          if (!vpRect) continue;
          let [vx1, vy1, vx2, vy2] = vpRect;
          // Normalizar (pueden venir invertidos)
          const leftPx = Math.min(vx1, vx2);
          const rightPx = Math.max(vx1, vx2);
          const topPx = Math.min(vy1, vy2);
          const bottomPx = Math.max(vy1, vy2);
          let w = rightPx - leftPx;
          let h = bottomPx - topPx;
          const originalH = h;
          // Heurística: si es una línea de texto muy fina, aplicar ajuste vertical hacia abajo
          let verticalFudge = 0;
          if (h <= 18) { // probable línea de texto (depende de escala)
            verticalFudge = 3; // desplazar 3px hacia abajo
            // mantén la parte superior y extiende hacia abajo
            h = h + verticalFudge;
          }
          // Expandir hacia abajo adicional si la altura queda muy pequeña todavía
          if (h < 12) {
            h += 4;
          }
          const pct = {
            left: (leftPx / viewport.width) * 100,
            top: ((topPx - (-verticalFudge)) / viewport.height) * 100, // topPx no cambia (el desplazamiento se refleja solo en la altura extra)
            width: (w / viewport.width) * 100,
            height: (h / viewport.height) * 100
          };
          links.push({
            url: linkUrl,
            dest: dest,
            destPageNumber: destPageNumber,
            pdfRect: annotation.rect.slice(),
            viewportRect: [leftPx, topPx, leftPx + w, topPx + h],
            pct,
            meta: { originalH, verticalFudge }
          });
        }
      }

      return {
        pageNumber,
        imageUrl: imageDataUrl,
        width: viewport.width,
        height: viewport.height,
        pdfWidth: viewportBase.width,
        pdfHeight: viewportBase.height,
        renderScale,
        links
      };
    } catch (err) {
      console.error(`Error rendering page ${pageNumber}:`, err);
      return null;
    }
  };

  // Memoizar la lista de páginas para no desencadenar reconciliaciones agresivas del DOM del flipbook
  const flipBookPages = useMemo(() => {
    return pages.map((pageData, idx) => (
      <Page key={pageData?.pageNumber ?? idx} pageData={pageData} className="page" flipBookRef={flipBookRef} />
    ));
  }, [pages]);

  const flipBookComponent = useMemo(() => {
    if (isFullscreen && fullscreenHeight) {
      const ratio = pages[0] ? pages[0].width / pages[0].height : 0.75;
      const pageHeight = Math.min(fullscreenHeight - 20, window.innerHeight - 80);
      const pageWidth = Math.min(pageHeight * ratio, (window.innerWidth / 2) - 100);
      const pageHeightAdj = Math.round(pageHeight + 12); // small buffer to avoid clipping
      console.debug('[flipbook] fullscreen heights', { pageHeight, pageHeightAdj, pageWidth });
      return (
        <HTMLFlipBook
          ref={flipBookRef}
          width={Math.round(pageWidth)}
          height={pageHeightAdj}
          size="stretch"
          minWidth={320}
          maxWidth={1100}
          minHeight={400}
          maxHeight={1800}
          maxShadowOpacity={0.5}
          showCover={true}
          mobileScrollSupport={true}
          className="flipbook"
          startPage={0}
          drawShadow={true}
          flippingTime={600}
          usePortrait={true}
          startZIndex={10}
          autoSize={true}
          clickEventForward={false}
          useMouseEvents={true}
          swipeDistance={30}
          showPageCorners={true}
          disableFlipByClick={false}
        >
          {flipBookPages}
        </HTMLFlipBook>
      );
    }
    // Mobile responsive sizing - Ajustado para ocupar más espacio vertical
    if (isMobile) {
      // Usar todo el ancho disponible con un margen mínimo
      const mobileWidth = Math.min(window.innerWidth - 8, 420);
      // Calcular altura basada en el ratio real de las páginas del PDF
      const pageRatio = pages[0] ? pages[0].height / pages[0].width : 1.414;
      const mobileHeightFromRatio = Math.round(mobileWidth * pageRatio);
      // No exceder el espacio disponible en pantalla
      const maxAvailable = window.innerHeight - 110;
      const mobileHeight = Math.min(mobileHeightFromRatio, maxAvailable);
      console.debug('[flipbook] mobile dimensions', { mobileWidth, mobileHeight, pageRatio });
      return (
        <HTMLFlipBook
          ref={flipBookRef}
          width={mobileWidth}
          height={mobileHeight}
          size="stretch"
          minWidth={240}
          maxWidth={450}
          minHeight={320}
          maxHeight={780}
          maxShadowOpacity={0.5}
          showCover={true}
          mobileScrollSupport={true}
          className="flipbook"
          startPage={0}
          drawShadow={true}
          flippingTime={600}
          usePortrait={true}
          startZIndex={10}
          autoSize={true}
          clickEventForward={false}
          useMouseEvents={true}
          swipeDistance={30}
          showPageCorners={true}
          disableFlipByClick={false}
        >
          {flipBookPages}
        </HTMLFlipBook>
      );
    }
    // non-fullscreen default size (add small vertical buffer to avoid 1px/2px clipping)
    const defaultPageHeight = 480;
    const defaultPageHeightAdj = defaultPageHeight + 12;
    console.debug('[flipbook] normal heights', { defaultPageHeight, defaultPageHeightAdj });
    return (
      <HTMLFlipBook
        ref={flipBookRef}
        width={380}
        height={defaultPageHeightAdj}
        size="stretch"
        minWidth={280}
        maxWidth={450}
        minHeight={330}
        maxHeight={550}
      maxShadowOpacity={0.5}
      showCover={true}
      mobileScrollSupport={true}
      className="flipbook"
      startPage={0}
      drawShadow={true}
      flippingTime={600}
      usePortrait={true}
      startZIndex={10}
      autoSize={true}
      clickEventForward={false}
      useMouseEvents={true}
      swipeDistance={30}
      showPageCorners={true}
      disableFlipByClick={false}
      >
        {flipBookPages}
      </HTMLFlipBook>
    );
  }, [flipBookPages, isFullscreen, fullscreenHeight, pages, isMobile]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8" style={{ minHeight: 260 }} role="status" aria-live="polite">
        <div className="w-full max-w-lg bg-gray-900 text-white rounded-xl shadow-2xl p-8 flex items-center gap-6">
          <div className="flex-shrink-0">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-t-transparent border-white/60" aria-hidden="true"></div>
          </div>

          <div>
            <h3 className="text-2xl font-semibold">Cargando documento...</h3>
            <p className="text-gray-300 mt-2">Por favor espera mientras preparamos las páginas para su visualización.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <div className="text-red-400 text-center">
          <p className="text-xl mb-2">❌ Error</p>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (pages.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <p className="text-gray-300">No se encontraron páginas en el PDF.</p>
      </div>
    );
  }

  return (
    // Contenedor principal del "Reader" con fondo gris
    <div
      ref={containerRef}
      className={`reader-container bg-gray-800 rounded-lg shadow-2xl ${isFullscreen ? 'fullscreen' : ''}`}
      style={{ 
          width: isFullscreen ? '100vw' : (isMobile ? '100vw' : '95vw'),
          height: isFullscreen ? '100vh' : (isMobile ? 'auto' : '90vh'),
          maxWidth: isFullscreen ? '100vw' : (isMobile ? '100vw' : '1200px'),
          display: 'flex',
          flexDirection: 'column',
          position: isFullscreen ? 'fixed' : undefined,
          inset: isFullscreen ? 0 : undefined,
          zIndex: isFullscreen ? 9999 : undefined,
          borderRadius: isFullscreen ? 0 : (isMobile ? 0 : undefined),
          // Añadir padding horizontal en fullscreen para separar controles de los bordes
      paddingTop: isFullscreen ? 4 : (isMobile ? 0 : 20),
      paddingLeft: isFullscreen ? 20 : (isMobile ? 0 : undefined),
      paddingRight: isFullscreen ? 20 : (isMobile ? 0 : undefined),
          // Reserva espacio inferior solo si realmente hace falta (evita gap extra en modo normal)
          paddingBottom: needBottomPadding && controlBarHeight ? controlBarHeight + 8 : undefined,
          margin: isMobile ? 0 : undefined,
        }}
    >
      {/* Layout principal usando CSS Grid para tres columnas */}
      <div 
        className="relative w-full flex-1"
        style={{ 
          display: isMobile ? 'flex' : 'grid',
          gridTemplateColumns: isMobile ? undefined : (isFullscreen ? 'minmax(32px, 1fr) minmax(680px, 1600px) minmax(32px, 1fr)' : '1fr minmax(760px, max-content) 1fr'),
          gridTemplateRows: isMobile ? undefined : '1fr',
          flexDirection: isMobile ? 'column' : undefined,
          alignItems: 'center',
          justifyItems: isMobile ? undefined : 'center',
          justifyContent: isMobile ? 'flex-start' : 'center',
          gap: isMobile ? '0' : (isFullscreen ? '12px' : '20px'),
          padding: isMobile ? '0' : (isFullscreen ? '4px 0 0 0' : '20px 0'),
          height: isFullscreen && fullscreenHeight ? `${fullscreenHeight}px` : (isMobile ? 'auto' : undefined),
          flex: isMobile ? undefined : undefined,
          minHeight: isMobile ? '0' : undefined
        }}
      >
        
        {/* Flecha izquierda - oculta en móvil */}
        {!isMobile && (
          <div className="flex items-center justify-center h-full">
            <button
              onClick={() => flipBookRef.current?.pageFlip().flipPrev()}
              className="nav-button bg-black bg-opacity-40 hover:bg-opacity-60 text-white rounded-full shadow-lg transition-all duration-200 flex items-center justify-center"
              style={{ width: isFullscreen ? '64px' : '48px', height: isFullscreen ? '64px' : '48px' }}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          </div>
        )}

        {/* Área central del Flipbook - Con overflow visible para permitir expansión */}
        <div 
          className="flipbook-wrapper"
          style={{ 
            width: '100%',
            height: isMobile ? 'auto' : '100%',
            flex: isMobile ? '1' : undefined,
            maxHeight: isMobile ? undefined : (contentAvailable ? `${contentAvailable}px` : 'calc(100% - 60px)'),
            display: 'flex',
            alignItems: isMobile ? 'flex-start' : 'center',
            justifyContent: 'center'
          }}
        >
          {flipBookComponent}
        </div>

        {/* Flecha derecha - oculta en móvil */}
        {!isMobile && (
          <div className="flex items-center justify-center h-full">
            <button
              onClick={() => flipBookRef.current?.pageFlip().flipNext()}
              className="nav-button bg-black bg-opacity-40 hover:bg-opacity-60 text-white rounded-full shadow-lg transition-all duration-200 flex items-center justify-center"
              style={{ width: isFullscreen ? '64px' : '48px', height: isFullscreen ? '64px' : '48px' }}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}

      </div>
      
      {/* Barra de controles inferior (debajo del contenido) */}
      <div className="enhanced-control-bar" ref={controlBarRef}>
        {/* Slot izquierdo para futuros controles */}
        <div className="control-slot-left"></div>
        
        {/* Centro: información de páginas mejorada */}
        <div className="page-info-center">
          <div className="page-counter">
            {pages.length} páginas
          </div>
          <div className="progress-indicator">
            <div className="progress-dots">
              {Array.from({ length: Math.min(pages.length, 10) }, (_, i) => (
                <div key={i} className="dot"></div>
              ))}
              {pages.length > 10 && <span className="more-indicator">...</span>}
            </div>
          </div>
        </div>
        
        {/* Slot derecho para futuros controles */}
        <div className="control-slot-right">
          <button
            className="fullscreen-btn"
            title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
            type="button"
            onClick={toggleFullscreen}
          >
            {isFullscreen ? (
              /* Icono SALIR fullscreen: mismas proporciones y grosor que el de entrar */
              <svg
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
                preserveAspectRatio="xMidYMid meet"
              >
                {/* Cuatro esquinas internas (colapso) */}
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H5v4M15 5h4v4M9 19H5v-4M15 19h4v-4"
                />
              </svg>
            ) : (
              /* Icono ENTRAR fullscreen: cuatro esquinas externas (expansión) */
              <svg
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
                preserveAspectRatio="xMidYMid meet"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 9V5h4M20 9V5h-4M4 15v4h4M20 15v4h-4"
                />
              </svg>
            )}
          </button>
        </div>
      </div>
      {/* Floating debug export button */}
      {/* Debug export button removed for production */}
    </div>
  );
};

export default Flipbook;

// Debugging helpers removed for production build